/**
 * Cart Store — Stato globale carrello condiviso tra header, drawer, course-page, purchase-drawer.
 *
 * Uso nei template:
 *   $store.cart.count          — numero articoli
 *   $store.cart.openDrawer()   — apre il cart drawer
 *   $store.cart.addItem(body)  — aggiunge un corso (POST /v1/cart)
 */
export function registerCartStore(Alpine) {
    Alpine.store('cart', {
        // Stato
        count: 0,
        items: [],
        subtotal: 0,
        discount: 0,
        tax: 0,
        stampTax: 0,
        total: 0,
        totalCredits: 0,
        promoText: '',
        discountLabel: '',
        isGlobalCard: false,
        gcInCart: false,
        globalCardCoursesCount: null,
        creditVoucher: null,
        creditVouchers: [],
        referralCode: null,
        loading: false,
        drawerOpen: false,
        _fetched: false,
        _fetching: false,

        init() {
            // Codice amico da sessionStorage (impostato da /promo/{code})
            this.referralCode = sessionStorage.getItem('ik_referral_code') || null;

            // Sincronizza cart token da PHP session → localStorage
            const serverToken = window.IKOS?.cartToken;
            if (serverToken) {
                localStorage.setItem('ik_cart_token', serverToken);
            }

            // Conteggio iniziale da SSR (evita flash badge vuoto)
            this.count = parseInt(window.IKOS?.cartCount, 10) || 0;

            // Dopo login/register con redirect: ri-fetcha per sincronizzare badge
            if (sessionStorage.getItem('ik_auth_just_logged')) {
                sessionStorage.removeItem('ik_auth_just_logged');
                this.refresh();
            } else if (this.count > 0) {
                // Carrello non vuoto al load: carica gli articoli una volta così le
                // card del listing riflettono subito lo stato "nel carrello"
                // (pulsante "Rimuovi dal carrello"). Se vuoto, resta lazy.
                this.fetchCart();
            }
        },

        /**
         * Carica il carrello completo dall'API.
         */
        async fetchCart() {
            if (this._fetching) return;
            this._fetching = true;
            this.loading = true;

            try {
                const data = await this._cartFetch('GET');
                this._applyCartData(data);
                await this._enrichResItems();
                this._fetched = true;
            } catch (e) {
                console.error('cart.fetchCart:', e);
            } finally {
                this.loading = false;
                this._fetching = false;
            }
        },

        /**
         * Aggiunge un corso al carrello.
         * @param {Object} body — { courses_id, coupon?, disabled_credit?, agreements_id?, agreement_code?, agreement_user? }
         * @returns {Promise<Object>} — risposta API
         */
        async addItem(body) {
            const data = await this._cartFetch('POST', body);

            // Auto-checkout: se il carrello era gratis, l'API ritorna orders_id
            if (data.orders_id) {
                // La navigazione distrugge il contesto JS prima che un evento parta:
                // il tracking lo legge all'init della pagina di arrivo (hooks.js).
                try { sessionStorage.setItem('ik_free_order', JSON.stringify({ orders_id: data.orders_id, courses_id: body.courses_id })); } catch (e) {}
                window.location.href = '/conferma-ordine.php?id=' + data.orders_id;
                return data;
            }

            this._applyCartData(data);
            await this._enrichResItems();
            this._fetched = true;

            // Apre il drawer
            this.drawerOpen = true;
            window.dispatchEvent(new CustomEvent('cart-opened'));

            return data;
        },

        /**
         * Rimuove un corso dal carrello.
         * @param {number} courseId
         */
        async removeItem(courseId) {
            this.loading = true;

            try {
                const data = await this._cartFetch('DELETE', { courses_id: courseId });
                this._applyCartData(data);
                await this._enrichResItems();
            } catch (e) {
                console.error('cart.removeItem:', e);
            } finally {
                this.loading = false;
            }
        },

        /**
         * Apre il drawer (lazy fetch al primo open).
         */
        openDrawer() {
            this.drawerOpen = true;
            document.body.style.overflow = 'hidden';

            if (!this._fetched) {
                this.fetchCart();
            }
        },

        /**
         * Applica un codice promozione via PATCH /v1/cart.
         * @param {string} code
         */
        async applyPromotion(code) {
            try {
                const data = await this._cartFetch('PATCH', { promotion_code: code });
                this._applyCartData(data);
                await this._enrichResItems();
            } catch (e) {
                console.error('cart.applyPromotion:', e);
                throw e;
            }
        },

        /**
         * Rimuove il codice promozione attivo via PATCH /v1/cart.
         */
        async removePromotion() {
            try {
                const data = await this._cartFetch('PATCH', { remove_promotion: true });
                this._applyCartData(data);
                await this._enrichResItems();
            } catch (e) {
                console.error('cart.removePromotion:', e);
            }
        },

        /**
         * Resetta lo stato e ri-fetcha il carrello dall'API.
         * Da chiamare dopo cambio stato auth (login/register inline).
         */
        async refresh() {
            this._fetched = false;
            await this.fetchCart();
        },

        /**
         * True se almeno un corso (non global_card, non package) ha disabled_credit per no_disciplina.
         */
        get hasProfessionMismatch() {
            return this.items.some(i =>
                i.product_type !== 'global_card' &&
                i.product_type !== 'package' &&
                i.disabled_credit &&
                Array.isArray(i.disabled_credit_reasons) &&
                i.disabled_credit_reasons.includes('no_disciplina')
            );
        },

        /**
         * Chiude il drawer.
         */
        closeDrawer() {
            this.drawerOpen = false;
            // Ripristina scroll body solo se anche checkout è chiuso
            if (!Alpine.store('checkout')?.shifting) {
                document.body.style.overflow = '';
            }
        },

        /**
         * Formato testo luogo per corsi residenziali (allineato alla scheda corso).
         * Es: "Il corso si terrà il 15 maggio 2026 in Via L. Galvani, 12 a Milano"
         * Città che inizia per vocale: "... in Piazza De André, n.76 ad Alessandria"
         * Corsi su più giorni: "Il corso si terrà dal 15 al 17 maggio 2026 in ..."
         * Senza sede fisica (es. webinar): "Il corso si terrà online dal 15 al 17 maggio 2026"
         */
        formatLocation(item) {
            const dates = this.formatCourseDates(item.start_at, item.end_at);
            const address = (item.address || '').trim();
            const city = (item.city || '').trim();
            if (!address && !city) return dates ? 'Il corso si terrà online ' + dates : '';

            const parts = [];
            if (dates) parts.push('Il corso si terrà ' + dates);
            if (address) parts.push('in ' + address);
            if (city) parts.push((/^[aeiouàèéìòù]/i.test(city) ? 'ad ' : 'a ') + city);
            return parts.join(' ');
        },

        /**
         * Formatta la data (o l'intervallo) di svolgimento di un corso residenziale.
         * Omette le parti ridondanti quando mese e/o anno coincidono:
         *   "il 15 maggio 2026"
         *   "dal 15 al 17 maggio 2026"
         *   "dal 30 aprile al 2 maggio 2026"
         *   "dal 30 dicembre 2026 al 2 gennaio 2027"
         * @param {?string} startAt data ISO 'YYYY-MM-DD'
         * @param {?string} endAt   data ISO 'YYYY-MM-DD' (opzionale)
         * @returns {string}
         */
        formatCourseDates(startAt, endAt) {
            if (!startAt) return '';
            const start = new Date(startAt + 'T00:00:00');
            if (isNaN(start)) return '';

            const full = (d) => d.toLocaleDateString('it-IT', {
                day: 'numeric', month: 'long', year: 'numeric'
            });

            const end = endAt ? new Date(endAt + 'T00:00:00') : null;
            // Nessuna data fine, uguale o antecedente all'inizio → corso di un solo giorno
            if (!end || isNaN(end) || end <= start) return 'il ' + full(start);

            const sameYear = start.getFullYear() === end.getFullYear();
            const sameMonth = sameYear && start.getMonth() === end.getMonth();

            let from;
            if (sameMonth) {
                from = start.toLocaleDateString('it-IT', { day: 'numeric' });
            } else if (sameYear) {
                from = start.toLocaleDateString('it-IT', { day: 'numeric', month: 'long' });
            } else {
                from = full(start);
            }

            return 'dal ' + from + ' al ' + full(end);
        },

        /**
         * Formato prezzo italiano: "49,90 €"
         */
        formatPrice(value) {
            if (value == null) return '';
            return parseFloat(value).toLocaleString('it-IT', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
            }) + '\u00A0\u20AC';
        },

        // ── Helpers privati ──────────────────────────────────

        /**
         * Arricchisce gli item RES con address da GET /v1/courses/{id}.
         * Chiamate in parallelo, errori silenziosi.
         */
        async _enrichResItems() {
            const apiBase = window.IKOS?.apiBase || '';
            const resItems = this.items.filter(i => i.type !== 'FAD' && i.id);
            console.log('[cart] _enrichResItems: resItems=', resItems.length, 'apiBase=', apiBase, 'items=', this.items.map(i => ({id:i.id, type:i.type, city:i.city, start_at:i.start_at, address:i.address})));
            if (!resItems.length) return;

            await Promise.all(resItems.map(async (item) => {
                try {
                    const res = await fetch(`${apiBase}/v1/courses/${item.id}`, {
                        headers: { 'Accept': 'application/json' }
                    });
                    if (!res.ok) return;
                    const data = await res.json();
                    item.address = data.address || null;
                    // Fallback: se il carrello non ha le date, prendile dal dettaglio corso
                    if (!item.start_at && data.start_at) item.start_at = data.start_at;
                    if (!item.end_at && data.end_at) item.end_at = data.end_at;
                } catch (e) {
                    // silenzioso: l'item resta senza address
                }
            }));

            // Forza reattività Alpine riassegnando l'array
            this.items = [...this.items];
        },

        /**
         * Applica i dati del carrello dalla risposta API allo store.
         */
        _applyCartData(data) {
            this.items = data.items || [];
            this.count = data.quantity || 0;
            this.subtotal = data.subtotal || 0;
            this.discount = data.discount || 0;
            this.tax = data.tax || 0;
            this.stampTax = data.stamp_tax || 0;
            this.total = data.total || 0;
            this.totalCredits = data.total_credits || 0;
            this.promoText = data.promo_text || '';
            this.discountLabel = data.discount_label || '';
            this.isGlobalCard = data.is_global_card || false;
            this.gcInCart = (data.items || []).some(i => i.product_type === 'global_card');
            this.globalCardCoursesCount = data.global_card_courses_count ?? null;
            this.creditVoucher  = data.credit_voucher  || null;
            this.creditVouchers = data.credit_vouchers || [];


            // Sincronizza conteggio in cookie per SSR badge header
            document.cookie = 'ik_cart_count=' + this.count + ';path=/;max-age=2592000;SameSite=Lax';
        },

        /**
         * Fetch wrapper per le API carrello con gestione X-Cart-Token.
         */
        async _cartFetch(method, body) {
            const apiBase = window.IKOS?.apiBase || '';
            const url = `${apiBase}/v1/cart`;

            const headers = {
                'Accept': 'application/json',
                'Content-Type': 'application/json',
            };

            // Auth token per utenti loggati
            const authToken = window.IKOS?.authToken;
            if (authToken && authToken !== true) {
                headers['Auth-Token'] = authToken;
            }

            // Cart token per guest
            const token = localStorage.getItem('ik_cart_token');
            if (token) {
                headers['X-Cart-Token'] = token;
            }

            const options = { method, headers };

            if (body && method !== 'GET') {
                options.body = JSON.stringify(body);
            }

            let res;
            try {
                res = await fetch(url, options);
            } catch (e) {
                throw { _network: true, message: 'Errore di connessione' };
            }

            // Salva token dalla response
            const responseToken = res.headers.get('X-Cart-Token');
            if (responseToken) {
                localStorage.setItem('ik_cart_token', responseToken);
            }

            const data = await res.json().catch(() => ({}));

            if (!res.ok) {
                throw data;
            }

            return data;
        },
    });
}
